package com.schoolconnect.app.repository.analytics

import com.schoolconnect.app.datasource.network.KtorApiClient
import com.schoolconnect.app.datasource.network.response.SchoolAnalyticsResponse
import com.schoolconnect.app.repository.common.CachedValue
import com.schoolconnect.app.repository.common.loadRefreshableCachedValue
import com.schoolconnect.app.repository.common.tenantCacheKey
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob

object SchoolAnalyticsRepository {
    private const val TAG = "[DATA_RETRIEVE_SC] SchoolAnalyticsRepository"

    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private val cachedAnalytics = mutableMapOf<String, CachedValue<SchoolAnalyticsResponse>>()

    suspend fun loadSchoolAnalytics(
        schoolCode: String,
        forceRemote: Boolean = false
    ): Result<SchoolAnalyticsResponse> {
        val normalizedSchoolCode = schoolCode.trim()
        if (normalizedSchoolCode.isBlank()) {
            return Result.failure(IllegalArgumentException("School code is required for analytics"))
        }
        val key = tenantCacheKey("school-analytics:$normalizedSchoolCode")

        return loadRefreshableCachedValue(
            scope = scope,
            tag = TAG,
            operation = "loadSchoolAnalytics",
            context = "school=$key",
            cachedValue = if (forceRemote) null else cachedAnalytics[key],
            fetchValue = { KtorApiClient.getSchoolAnalytics(normalizedSchoolCode) },
            updateCache = { response, fetchedAt ->
                cachedAnalytics[key] = CachedValue(response, fetchedAt)
            },
            successDetails = { response ->
                val metrics = response.metrics
                "school=$normalizedSchoolCode students=${metrics.students.total} teachers=${metrics.staff.teachers.total} schoolStaff=${metrics.staff.supportStaff.total} academicYears=${metrics.academicYears.total} subjects=${metrics.subjects.total}"
            }
        )
    }

    fun invalidate(schoolCode: String) {
        cachedAnalytics.remove(tenantCacheKey("school-analytics:${schoolCode.trim()}"))
    }

    fun invalidateAll() {
        cachedAnalytics.clear()
    }
}
